import React, { Key } from 'react';

import Tooltip, { TooltipProps } from 'antd/es/tooltip';

import { WithId } from '@common/typescript/objects/WithId';

import '@common/react/scss/components/listWithTooltip.scss';

export type GetKeyDelegate<T> = (item: T, index: number) => Key;
export type GetValueDelegate<T> = (item: T) => React.ReactNode;

type ListWithTooltipEitherProperty<T> = {
	getValue?: never;
	customNode: GetValueDelegate<T>;
} | {
	getValue: GetValueDelegate<T>;
	customNode?: never;
}

export type ListWithTooltipEitherProps<T, TInterface> = Omit<TInterface, 'getValue' | 'customNode'>
	& ListWithTooltipEitherProperty<T>;

interface BaseListWithTooltipProps<T> {
	/**
	 * array of items
	 */
	array: Array<T>;
	/**
	 * render inside <div className="inline-select-item inline-select-item_sm">{}</div>
	 */
	getValue: GetValueDelegate<T>;
	/**
	 * function to get custom key for element
	 */
	getKey: GetKeyDelegate<T>;
	/**
	 * maximum number of elements outside the tooltip
	 * @default 1
	 */
	count?: number;
	/**
	 * function to get custom node
	 */
	customNode?: GetValueDelegate<T>;
	/**
	 * function to get custom node for element inside tooltip
	 * @default customNode
	 */
	customTooltipNode?: GetValueDelegate<T>;
	/**
	 * callback to handle onClick at default element
	 */
	onClick?: (item: T) => void;
	/**
	 * div container class name
	 * @default ''
	 */
	containerClassName?: string;
	/**
	 * antd tooltip props
	 */
	tooltipProps?: Omit<TooltipProps, 'children' | 'title'>;
	/**
	 * markup for case when array is empty
	 * @default null
	 */
	emptyNode?: React.ReactNode;
}

/**
 * BaseListWithTooltip component
 *
 * @template T - type of items
 * @param props - {@link BaseListWithTooltipProps}
 * @returns React.ReactElement
 */
const BaseListWithTooltip = <T, >(
	props: ListWithTooltipEitherProps<T, BaseListWithTooltipProps<T>>,
) => {
	const {
		array,
		getValue,
		customNode,
		getKey,
		count = 1,
		onClick,
		customTooltipNode = customNode,
		tooltipProps,
		containerClassName = '',
		emptyNode = null,
	} = props;

	if (!array?.length) {
		return <>{emptyNode}</>;
	}

	return <div className={containerClassName}>
		{array.slice(0, count).map((item, index) =>
			<React.Fragment key={getKey(item, index)}>
				{customNode ? customNode(item)
					: <div className="inline-select-item inline-select-item_sm">
						<span onClick={() => { onClick && onClick(item); }}>{getValue(item)}</span>
					</div>}
			</React.Fragment>)
		}
		{array.length > count && (
			<Tooltip
				overlayClassName="list-tooltip"
				{...tooltipProps}
				title={array.slice(count).map((item, index) =>
					<React.Fragment key={getKey(item, index)}>
						{customTooltipNode ? customTooltipNode(item)
							: <div className="inline-select-item inline-select-item_sm" key={getKey(item, index)}>
								<span onClick={() => { onClick && onClick(item); }}>{getValue?.(item)}</span>
							</div>}
					</React.Fragment>)}
			>
				<span className="inline-select-item inline-select-item_sm">
					+
					{array.slice(count).length}
					...
				</span>
			</Tooltip>
		)}
	</div>;
};

interface ListWithTooltipProps<T extends WithId> extends Omit<BaseListWithTooltipProps<T>, 'getKey'> {
	getKey?: GetKeyDelegate<T>;
}

/**
 * ListWithTooltip component
 *
 * @template {WithId} T - type of items
 * @param props - {@link ListWithTooltipProps}
 * @returns React.ReactElement
 */
export const ListWithTooltip = <T extends WithId, >(
	props: ListWithTooltipEitherProps<T, ListWithTooltipProps<T>>,
) => {
	const defaultProps = {
		getKey: (item: T, index: number) => `${item?.id}${index}`,
	};

	return <BaseListWithTooltip {...defaultProps}{...props} />;
};

interface ListWithTooltipForEnumProps<T extends number> extends Omit<BaseListWithTooltipProps<T>, 'getValue' | 'getKey'> {
	options: Partial<Record<T, string>>;
	getKey?: GetKeyDelegate<T>;
	getValue?: GetValueDelegate<T>;
}

/**
 * ListWithTooltipForEnum component
 *
 * @template {number} T - enum
 * @param props - {@link ListWithTooltipForEnumProps}
 * @returns React.ReactElement
 */
export const ListWithTooltipForEnum = <T extends number>(
	props: ListWithTooltipForEnumProps<T>,
) => {
	const {
		options,
		...rest
	} = props;

	const defaultProps = {
		getValue: (item: T) => options[item],
		getKey: (item: T) => `${item}`,
	};

	return <BaseListWithTooltip {...defaultProps}{...rest} customNode={undefined} />;
};
